<template>
  <div
    id="app"
    class="darwin-theme"
  >
    <router-view />

    <portal-target
      name="modal"
      multiple
    />

    <Toast />
    <CriticalErrorDialog
      v-if="hasFatalError"
      @close="fatalError.reset"
    />
  </div>
</template>

<script lang="ts" setup>
import { watch, computed, onMounted } from 'vue'

import { useInitialAuthLoader } from '@/modules/Auth/useInitialAuthLoader'
import { useAnnotationTypeLoader } from '@/modules/Classes/useAnnotationTypeLoader'
import { useClassLoader } from '@/modules/Classes/useClassLoader'
import { useInstructionsLoader } from '@/modules/Rules/useInstructionsLoader'
import { useIntercomLoader } from '@/composables/useIntercomLoader'
import { useIntercomMessaging } from '@/composables/useIntercomMessaging'
import { useSentryWatchers } from '@/composables/useSentryWatchers'
import { useNotificationsChannel } from './components/Layout/Notification/useNotificationsChannel'
import CriticalErrorDialog from '@/modules/Workview/CriticalErrorDialog.vue'
import { useFatalError } from '@/modules/Workview/useFatalError'
import { useTeamStore } from '@/pinia/useTeamStore'
import Toast from '@/uiKit/Toast/Toast.vue'

import { useModelsStore } from '@/modules/Models/useModelsStore'
import { usePlanLoader } from './modules/Billing/usePlanLoader'
import { useAuthStore } from './modules/Auth/useAuthStore'
import { useUserStore } from './modules/Auth/useUserStore'
import { useModelCreationStore } from './modules/Models/useModelCreationStore'

useInitialAuthLoader()
useSentryWatchers()
useIntercomLoader()

const typeLoader = useAnnotationTypeLoader()
onMounted(() => typeLoader.load())

useClassLoader()
useInstructionsLoader()
useIntercomMessaging()

const planLoader = usePlanLoader()
planLoader.autoLoadPlanOnTeamChange()

const teamStore = useTeamStore()

const fatalError = useFatalError()

const hasFatalError = computed(() => fatalError.hasFatalError)

const modelsStore = useModelsStore()
const modelCreationStore = useModelCreationStore()

watch(
  () => teamStore.currentTeam,
  () => {
    modelsStore.reset()
    modelCreationStore.reset()
  },
)

const userStore = useUserStore()
const authStore = useAuthStore()
const notificationChannel = useNotificationsChannel()

watch(
  () => [userStore.currentUser?.id, authStore.authenticated] as const,
  ([userId, isAuthenticated]) => {
    if (userId && isAuthenticated) {
      notificationChannel.join(userId)
    }

    if (!userId || !isAuthenticated) {
      notificationChannel.leave()
    }
  },
)
</script>
